<script>
import formInput from "@wielink-application/shared/js/mixins/formInput";
import assetMetadataValidity from "@wielink-application/asset/js/mixins/assetMetadataValidity";
import {useDropzone, convertFileToBase64} from '@wielink-application/shared/js/composables/useDropzone';
import AssetableModal from "@/pages/assets/AssetableModal.vue";
import Sortable from 'sortablejs';
import {v4 as uuid} from 'uuid';

export default {
    components: {AssetableModal},
    mixins: [formInput, assetMetadataValidity],
    props: {
        assetableType: {
            type: String,
            required: false,
            default: null
        },
        assetableGroup: {
            type: String,
            required: false,
            default: null
        }
    },
    inject: {
        registerAssetField: {default: null},
        unregisterAssetField: {default: null}
    },
    data() {
        return {
            prio: 0,
            key: uuid()
        }
    },
    mounted() {
        this.initialise();

        if (this.registerAssetField && this.assetableType && this.assetableGroup) {
            this.registerAssetField({
                name: this.name,
                assetableType: this.assetableType,
                assetableGroup: this.assetableGroup
            });
        }
    },
    beforeUnmount() {
        this.unregisterAssetField?.(this.name);
    },

    methods: {
        isDeferred() {
            return !!(this.assetableType && this.assetableGroup && this.registerAssetField);
        },

        initialise() {
            useDropzone({
                scope: this.$refs.dropzone,
                input: this.$refs.input,
                accept: 'image/*',
                onFiles: (files) => this.handleFiles(files),
                onRejected: () => Helpers.Message.error('U kunt hier alleen afbeeldingen in plaatsen.'),
            });

            new Sortable(this.$refs.list, {
                swapThreshold: 1,
                animation: 150,
                handle: '.sortable-handle',
                onEnd: (event) => {
                    let newArray = [];
                    let currentArray = this.modelValue;
                    $(this.$refs.list).find('.image-wrapper').each((index, element) => {
                        let $element = $(element);
                        let currentIndex = $element.data('currentIndex');
                        let asset = currentArray[currentIndex];
                        asset.prio = index;
                        newArray[index] = asset;
                    });
                    // Verwijderd gemarkeerde bestanden staan niet in de lijst, maar moeten behouden blijven
                    newArray.push(...currentArray.filter((asset) => asset.deleted));
                    this.changeModelValueTo(newArray);
                }
            });
        },

        async handleFiles(files) {
            if (!files || files.length < 1) {
                return;
            }

            let data = this.modelValue;

            if (!Array.isArray(data)) {
                data = [];
            }

            if (this.isDeferred()) {
                await this.fetchMetadataFields(this.assetableGroup);
            }

            let filesArray = Array.from(files);
            await Promise.all(filesArray.map(async (file) => {
                let base64 = await convertFileToBase64(file);
                let picture = {
                    id: null,
                    base64: base64,
                    lastModified: null,
                    size: null,
                    type: file.type,
                    name: file.name,
                    title: file.name,
                    prio: this.prio,
                };
                if (this.isDeferred()) {
                    picture.metadata = this.prepareMetadata({});
                }
                data.push(picture);

                this.prio++;
            }));

            this.changeModelValueTo(data);
        },

        edit(index) {
            this.$refs.modal.editAsset(this.modelValue[index], index);
        },

        async delete(index) {
            let result = await Helpers.Alert.confirm('Weet u zeker dat u dit bestand wilt verwijderen?');
            if (!result) {
                return;
            }

            let data = this.modelValue;
            if (this.isDeferred() && data[index].id) {
                data[index].deleted = true;
            } else {
                data.splice(index, 1);
            }
            this.changeModelValueTo(data);
        },

        _updateAsset(data) {
            let assets = this.modelValue;
            let asset = {...assets[data.index], ...data.asset};
            if (this.isDeferred() && asset.id) {
                asset.dirty = true;
            }
            assets[data.index] = asset;
            this.changeModelValueTo(assets);
        },

        async changeModelValueTo(value) {
            if (this.form) {
                delete this.form.errors[this.name];
            }

            this.$emit('update:modelValue', value);
            await this.$nextTick();
            this.key = uuid();
        },

        fileUrl(file) {
            if (file.base64) {
                return file.base64;
            }
            return route('api.v1.assets.preview', {asset: file.id})
        }
    }
}
</script>

<template>
    <div>
        <div ref="dropzone" class="upload-container">
            <div>
                <p class="m-0">Sleep bestanden hierheen of klik om te uploaden</p>
            </div>

            <input ref="input" :name="name" type="file" :id="renderedId" multiple hidden>

            <div :key="key" ref="list" class="d-flex justify-content-center flex-wrap gap-3" :class="{'pt-4' : modelValue.length > 0}" :id="renderedId + '-list'">
                <template v-for="(file, index) in modelValue">
                    <div v-if="!file.deleted" class="image-wrapper position-relative me-2 border" :data-current-index="index">
                        <img class="position-absolute top-0 start-0 w-100 h-100 object-fit-cover" style="object-position: center" :src="fileUrl(file)">

                        <i
                            v-if="missesRequiredMetadata(file)"
                            class="bi bi-exclamation-triangle-fill text-warning fs-5 position-absolute top-0 end-0 pe-1"
                            style="text-shadow: 0 0 3px rgba(0, 0, 0, .5)"
                            :title="metadataWarningHint"
                        ></i>

                        <div class="dropzone-image-overlay text-white position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center">
                            <div class="d-flex flex-column align-items-center">
                                <div class="d-flex gap-1">
                                    <a href="javascript:void(0);" @click.prevent="this.edit(index)" class="dropzone-action" aria-label="Bestand bewerken">
                                        <i class="bi bi-pencil-square"></i>
                                    </a>
                                    <a href="javascript:void(0);" @click.prevent="this.delete(index)" class="dropzone-action" aria-label="Bestand verwijderen">
                                        <i class="bi bi-trash"></i>
                                    </a>
                                    <a
                                        class="dropzone-action"
                                        :title="!file.id ? 'Om het bestand te kunnen downloaden moet u eerst het formulier opslaan.' : null"
                                        :class="{disabled: !file.id}" aria-label="Bestand downloaden"
                                        :href="file.id ? fileUrl(file) : null"
                                        :download="file.id ? 'download' : null"
                                    >
                                        <i class="bi bi-download"></i>
                                    </a>
                                </div>
                                <div>
                                    <a href="javascript:void(0);" class="dropzone-action sortable-handle" aria-label="Bestand verplaatsen">
                                        <i class="bi bi-arrows-move"></i>
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </template>
            </div>
        </div>

        <label v-if="hasError" class="text-danger mt-2 small">{{ this.form.errors[this.name] }}</label>
    </div>

    <AssetableModal
        :id="renderedId + '_edit_modal'"
        :assetable-group="assetableGroup"
        :assetable-type="assetableType"
        accept="image/*"
        ref="modal"
        @updated:asset="this._updateAsset"
    />
</template>